/* External */
import {
  createContext,
  memo,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { Record } from 'immutable';

/* Utils */
import { noOp } from '../../utils/functions';
import platform from '../../utils/platform';
import { createDeferredPromise } from '../../utils/createDeferredPromise';
import useShowDisconnectedMessageState from './useShowDisconnectedMessageState';

const ConnectionStatusRecord = Record({
  isConnected: false,
  connectionType: undefined,
});

const ConnectionStatusContext = createContext(ConnectionStatusRecord());
const UpdateConnectionStatusContext = createContext(noOp);
const ShowDisconnectedMessageContext = createContext(false);
const SetShowDisconnectedMessageContext = createContext(noOp);

export function useConnectionStatus() {
  return useContext(ConnectionStatusContext);
}

export function useUpdateConnectionStatus() {
  return useContext(UpdateConnectionStatusContext);
}

export function useShowDisconnectedMessage() {
  return useContext(ShowDisconnectedMessageContext);
}

export function useSetShowDisconnectedMessage() {
  return useContext(SetShowDisconnectedMessageContext);
}

let isConnected = false;
let waitConnectionDeferred = createDeferredPromise();
export function resolveWhenConnectionAvailable() {
  if (isConnected) {
    return Promise.resolve();
  }
  return waitConnectionDeferred.promise;
}

function ConnectionStatusProvider({ children }) {
  const [connectionStatus, setConnectionStatus] = useState(undefined);

  const updateConnectionStatus = useCallback(
    (subscribe) => {
      const onNetworkStateChange = (newState) => {
        setConnectionStatus((currentConnectionStatus) => (
          (currentConnectionStatus || ConnectionStatusRecord()).merge(newState)
        ));
      };

      platform.getConnectionStatus({
        subscribe,
        onSuccess: onNetworkStateChange,
        onFailure: (error) => {
          console.error('ConnectionStatusProvider>platform.getConnectionStatus failure', error);
        },
      });
    },
    [],
  );

  const {
    showDisconnectedMessage,
    setShowDisconnectedMessage,
  } = useShowDisconnectedMessageState(connectionStatus);
  const isFullyConnected = connectionStatus?.isConnected && !showDisconnectedMessage;

  useEffect(() => {
    updateConnectionStatus(true);
  }, [updateConnectionStatus]);

  useEffect(() => {
    if (isFullyConnected) {
      isConnected = true;
      waitConnectionDeferred.resolve();
    } else {
      isConnected = false;
      waitConnectionDeferred = createDeferredPromise();
    }
  }, [isFullyConnected]);

  return (
    <ConnectionStatusContext.Provider value={connectionStatus}>
      <UpdateConnectionStatusContext.Provider value={updateConnectionStatus}>
        <ShowDisconnectedMessageContext.Provider value={showDisconnectedMessage}>
          <SetShowDisconnectedMessageContext.Provider value={setShowDisconnectedMessage}>
            {children}
          </SetShowDisconnectedMessageContext.Provider>
        </ShowDisconnectedMessageContext.Provider>
      </UpdateConnectionStatusContext.Provider>
    </ConnectionStatusContext.Provider>
  );
}

ConnectionStatusProvider.propTypes = {
  children: PropTypes.node,
};

ConnectionStatusProvider.defaultProps = {
  children: undefined,
};

export default memo(ConnectionStatusProvider);
